import { InferType } from 'yup';
import _ from 'lodash';
import { useContext, useLayoutEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { formatApiError } from '@common/errors/error-formatting';
import { useResetPasswordMutation } from '@common/cache';
import { useForm } from 'react-hook-form';
import { resetPasswordSchema } from '../schemas';
import { ResetPasswordContext } from '../context';

type IFormShape = InferType<typeof resetPasswordSchema>;

export const usePasswordForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [initialValues] = useState({
    password: '',
    confirm: '',
  });
  const [httpError, setHttpError] = useState<string>('');

  const { setMFAToken, goToNextStep, setMFAType, setMaskedPhoneNumber } =
    useContext(ResetPasswordContext);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const mutation = useResetPasswordMutation();

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(resetPasswordSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  const submitHandler = async (values: IFormShape) => {
    try {
      setHttpError('');
      const response = await mutation.mutateAsync({
        password: values.password,
        token: token!,
      });

      setMFAToken(token!);
      // The generated types don't reflect the actual properties in the payload
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setMFAType((response as unknown as any).data.mfa_type);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setMaskedPhoneNumber((response as unknown as any).data.mfa_phone_number);

      goToNextStep();
    } catch (error) {
      setHttpError(formatApiError(error) ?? '');
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  useLayoutEffect(() => {
    try {
      const token = searchParams.get('token');

      if (!token) throw Error('No register token');

      setToken(token);
    } catch {
      navigate('/auth/login');
    }
  }, [searchParams, navigate]);

  return {
    showPassword,
    toggleShowPassword,
    hasError: mutation.isError,
    onSubmit,
    formMethods,
    loading: isSubmitting || mutation.isPending,
    httpError,
  };
};
